/**
 * EAConsentModal Component
 * CRITICAL: Shows BEFORE any EA analysis - user must explicitly consent
 *
 * Requirements (from P2 handoff):
 * - User must check "I consent to send this code" before analysis
 * - Consent is required for all EA analysis operations
 * - Shows clear privacy messaging about code transmission
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LockIcon from '@mui/icons-material/Lock';
import CodeIcon from '@mui/icons-material/Code';
import { getFileTypeConfig } from '../../../api/qbridgeEaApi';

/**
 * EAConsentModal Component
 * @param {boolean} open - Dialog visibility
 * @param {function} onClose - Close handler
 * @param {function} onConfirm - Confirm handler (called with consentGiven=true)
 * @param {object} file - Selected EA file
 * @param {string} analysisType - Type of analysis ('bugs' | 'quality' | 'performance' | 'risk' | 'full')
 * @param {boolean} isLoading - Loading state
 */
const EAConsentModal = ({
  open = false,
  onClose,
  onConfirm,
  file = null,
  analysisType = 'full',
  isLoading = false
}) => {
  const [consentChecked, setConsentChecked] = useState(false);

  // Reset consent when dialog opens
  useEffect(() => {
    if (open) {
      setConsentChecked(false);
    }
  }, [open]);

  if (!file) return null;

  const fileConfig = getFileTypeConfig(file.file_type);

  const analysisLabels = {
    bugs: 'Bug Detection',
    quality: 'Code Quality Analysis',
    performance: 'Performance Analysis',
    risk: 'Risk Analysis',
    full: 'Full Analysis'
  };

  const handleConfirm = () => {
    if (consentChecked && onConfirm) {
      onConfirm(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#0F0F14',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          maxWidth: '480px'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SecurityIcon sx={{ color: '#FFFFFF', fontSize: '1.5rem' }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: '#FFFFFF',
                fontSize: '1.125rem',
                fontWeight: 600
              }}
            >
              Consent Required
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.8125rem'
              }}
            >
              {analysisLabels[analysisType] || 'Analysis'}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              color: '#FFFFFF',
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ padding: '24px' }}>
        {/* File Info Card */}
        <Box
          sx={{
            padding: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CodeIcon sx={{ color: fileConfig.color, fontSize: '1.25rem' }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  fontFamily: "'JetBrains Mono', monospace",
                  wordBreak: 'break-all'
                }}
              >
                {file.name}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.8125rem',
                  mt: 0.25
                }}
              >
                {file.line_count?.toLocaleString() || '?'} lines
                {(typeof file.size_kb === 'number') && ` • ${file.size_kb.toFixed(1)} KB`}
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: `${fileConfig.color}20`,
                color: fileConfig.color,
                fontWeight: 700,
                fontSize: '0.6875rem',
                padding: '4px 8px',
                borderRadius: '4px',
                border: `1px solid ${fileConfig.color}40`
              }}
            >
              {fileConfig.label}
            </Box>
          </Box>
        </Box>

        {/* Privacy Notice */}
        <Alert
          severity="info"
          icon={<CloudUploadIcon sx={{ color: '#60A5FA' }} />}
          sx={{
            backgroundColor: 'rgba(59, 130, 246, 0.08)',
            color: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            mb: 3,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1 }}>
            Your code will be sent to Q.AI for analysis
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)' }}>
            The source code of this file will be transmitted securely to our AI service for analysis.
            Your code is processed in real-time and is not stored permanently.
          </Typography>
        </Alert>

        {/* Security Guarantees */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 1.5
            }}
          >
            Our Guarantees
          </Typography>

          {[
            { icon: LockIcon, text: 'Your original files are NEVER modified' },
            { icon: SecurityIcon, text: 'All transmissions are encrypted (TLS)' },
            { icon: CodeIcon, text: 'Modified versions saved with _QAI suffix only' }
          ].map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1
              }}
            >
              <item.icon sx={{ color: '#27B56D', fontSize: '1rem' }} />
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8125rem' }}>
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Consent Checkbox - CRITICAL */}
        <Box
          sx={{
            padding: '16px',
            backgroundColor: consentChecked
              ? 'rgba(39, 181, 109, 0.08)'
              : 'rgba(255, 255, 255, 0.02)',
            borderRadius: '12px',
            border: consentChecked
              ? '1px solid rgba(39, 181, 109, 0.3)'
              : '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.2s ease'
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                sx={{
                  color: 'rgba(255, 255, 255, 0.4)',
                  '&.Mui-checked': {
                    color: '#27B56D'
                  }
                }}
              />
            }
            label={
              <Typography
                sx={{
                  color: consentChecked ? '#FFFFFF' : 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                  lineHeight: 1.5
                }}
              >
                I consent to send this code to Q.AI for analysis
              </Typography>
            }
            sx={{
              alignItems: 'flex-start',
              margin: 0,
              '& .MuiFormControlLabel-label': {
                marginLeft: 0.5
              }
            }}
          />
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          padding: '16px 24px 24px',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          gap: 1.5
        }}
      >
        <Button
          onClick={onClose}
          disabled={isLoading}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            textTransform: 'none',
            fontWeight: 500,
            padding: '10px 20px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleConfirm}
          disabled={!consentChecked || isLoading}
          sx={{
            background: consentChecked
              ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)'
              : 'rgba(124, 58, 237, 0.3)',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 28px',
            borderRadius: '8px',
            minWidth: '160px',
            '&:hover': {
              background: consentChecked
                ? 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)'
                : 'rgba(124, 58, 237, 0.3)'
            },
            '&.Mui-disabled': {
              background: 'rgba(124, 58, 237, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)'
            }
          }}
        >
          {isLoading ? 'Starting...' : 'Start Analysis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EAConsentModal;
